import {
  callVisionModel,
  extractJson,
  SitePlanError,
  type VisionCallResult,
} from '@/reference/SitePlanClient';
import type { ReferencePrompt } from '@/reference/SitePlanClient';
import { defaultModelFor } from '@shared/ai/models';
import { parseWandSpell, WandFormatError, type WandSpell } from './WandOps';

/**
 * The wand's call.
 *
 * Everything about *talking to the API* — encoding images, JSON mode, truncation, HTTP failure
 * translation — is `callVisionModel`'s job and is not repeated here. What this module owns is the
 * part specific to the wand: which models are worth offering for it, and turning a reply into a
 * {@link WandSpell}.
 *
 * The model list differs from the reconstruction one on purpose. Reading scale and season off a
 * photograph is a once-per-site judgement where the flagship is obviously worth it; the wand is
 * called repeatedly, in a loop, while someone iterates. So the balanced tier is the default and the
 * flagship is one click away — the cost of a wrong wand result is an undo, not a wrong site.
 */

export interface WandModelOption {
  id: string;
  label: string;
  note: string;
}

export const WAND_MODELS: readonly WandModelOption[] = [
  {
    id: 'gpt-5.6-terra',
    label: 'GPT-5.6 Terra',
    note: 'Balanced. The right default for iterating — quick, and undo is one key away.',
  },
  {
    id: 'gpt-5.6-sol',
    label: 'GPT-5.6 Sol',
    note: 'Flagship. Worth it for a complex request, or when reading an attached photograph.',
  },
  {
    id: 'gpt-5.6-luna',
    label: 'GPT-5.6 Luna',
    note: 'Cheapest. Fine for simple, literal requests on one plant.',
  },
];

export const DEFAULT_WAND_MODEL = defaultModelFor('wand');

/**
 * Ceiling on the reply. Generous for a handful of ops with polygons, far short of a whole site — a
 * wand answer that needs more room than this has misunderstood the "smallest change" rule.
 */
const MAX_OUTPUT_TOKENS = 4000;

export interface WandRequest {
  apiKey: string;
  prompt: ReferencePrompt;
  /** Optional evidence photographs. The wand is usually text-only. */
  images?: readonly Blob[];
  model?: string;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
}

export interface WandResponse {
  spell: WandSpell;
  model: string;
  /** Raw reply text, kept so the UI can show exactly what came back. */
  raw: string;
  usage?: { promptTokens: number; completionTokens: number };
}

export async function castWandRequest(request: WandRequest): Promise<WandResponse> {
  const call: VisionCallResult = await callVisionModel({
    apiKey: request.apiKey,
    operation: 'wand',
    prompt: request.prompt,
    images: request.images ?? [],
    allowTextOnly: true,
    maxOutputTokens: MAX_OUTPUT_TOKENS,
    model: request.model?.trim() || DEFAULT_WAND_MODEL,
    ...(request.signal ? { signal: request.signal } : {}),
    ...(request.fetchImpl ? { fetchImpl: request.fetchImpl } : {}),
  });

  let spell: WandSpell;
  try {
    spell = parseWandSpell(JSON.parse(extractJson(call.raw)));
  } catch (error) {
    if (error instanceof WandFormatError) {
      throw new SitePlanError(`The wand's reply could not be read: ${error.message}`, null, true);
    }
    throw new SitePlanError('The wand did not return valid JSON.', null, true);
  }

  return {
    spell,
    model: call.model,
    raw: call.raw,
    ...(call.usage ? { usage: call.usage } : {}),
  };
}
